import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLanguage } from "../../contexts/LanguageContext";
import { AGENT_ENDPOINT } from "../../config";

const API_BASE = AGENT_ENDPOINT || "";

// How many message pairs (user + assistant) to send as history on each turn.
// 10 pairs = 20 messages — enough context for any realistic chat session
// while staying well under the model's context window.
const MAX_HISTORY_PAIRS = 10;

// Detect whether a string contains Arabic characters.
// Used to set dir="rtl" on individual message bubbles so mixed
// Arabic + LTR numbers/currency codes render in the correct order.
const ARABIC_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
function isArabicContent(text) {
  return typeof text === "string" && ARABIC_RE.test(text);
}

/**
 * SellerHub AI Assistant — floating chat widget.
 *
 * Protocol: POST /invocations with the SellerHub JWT + conversation history.
 * The agent responds over text/event-stream SSE:
 *   { type: "thought",      content }   — streaming reasoning tokens
 *   { type: "tool_call",    name, arguments }
 *   { type: "observation",  content }
 *   { type: "answer",       content }   — final reply
 *   { type: "error",        code, message }
 *   data: [DONE]                         — stream end sentinel
 *
 * Session lifecycle: threadId is generated once when the component mounts
 * (tied to the user's login session via Redux-persisted state). It is used
 * by the agent only for log correlation — no server-side session is stored.
 * Conversation history is managed here and sent in full on every request.
 */
export default function SellerHubChatWidget() {
  const { currentLanguage, isRTL } = useLanguage();
  const user = useSelector((state) => state.user?.data);
  const token = user?.token;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false); // true once the welcome message is shown
  const [expandedSteps, setExpandedSteps] = useState(new Set());

  // Abort controller ref — lets us cancel an in-flight fetch when the widget closes
  const abortRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Stable thread ID for the lifetime of this login session.
  // Generated once per component mount using the built-in Web Crypto API.
  const threadId = useMemo(() => crypto.randomUUID(), []);

  // Auto-scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);

  // Reset chat state when the user changes (logout / login with different account).
  // token changes from a value → undefined on logout, and undefined → new value on login.
  useEffect(() => {
    setMessages([]);
    setInput("");
    setIsReady(false);
    setIsOpen(false);
    abortRef.current?.abort();
  }, [token]);

  // Show welcome message the first time the panel opens
  const initChat = useCallback(() => {
    if (isReady) return;
    setIsReady(true);
    setMessages([
      {
        role: "assistant",
        isWelcome: true, // flagged so buildPayloadMessages never sends it to the agent
        content:
          currentLanguage === "ar"
            ? "مرحباً 👋 أنا مساعد **SellerHub**. اسألني عن مبيعاتك، إعلاناتك، طلباتك، أو منتجاتك — سأسحب المعلومات من تقاريرك. كيف أقدر أساعدك؟"
            : "Hi 👋 I'm your **SellerHub assistant**. Ask me about your sales, ads, orders, or products — I'll pull from your reports. What would you like to know?",
        suggestions:
          currentLanguage === "ar"
            ? ["كيف كانت الإيرادات آخر 7 أيام؟", "أعلى 5 منتجات هذا الشهر", "ما هو اتجاه ACOS؟"]
            : ["How did revenue do last 7 days?", "Top 5 products this month", "What's my ACOS trend?"],
      },
    ]);
  }, [isReady, currentLanguage]);

  const handleOpen = () => {
    setIsOpen(true);
    initChat();
  };

  /**
   * Build the messages array to send to the agent.
   * Only real user/assistant turns are included — welcome messages, error
   * messages, and local-only UI messages are excluded.
   * We trim to the last MAX_HISTORY_PAIRS pairs, then append the new user turn.
   */
  const buildPayloadMessages = useCallback(
    (newUserMessage) => {
      // Only include real conversation turns — not the welcome message, not error
      // messages (which are local UI only and not part of the conversation).
      const history = messages.filter(
        (m) => !m.isWelcome && !m.isError && (m.role === "user" || m.role === "assistant")
      );

      // Keep the last MAX_HISTORY_PAIRS pairs (each pair = 1 user + 1 assistant)
      const maxMessages = MAX_HISTORY_PAIRS * 2;
      const trimmed = history.slice(-maxMessages);

      return [
        ...trimmed.map((m) => ({ role: m.role, content: m.content })),
        { role: "user", content: newUserMessage },
      ];
    },
    [messages]
  );

  /**
   * Parse the SSE stream from /invocations.
   *
   * The agent emits newline-delimited events in the format:
   *   data: <JSON>\n\n
   *   data: [DONE]\n\n
   *
   * We accumulate partial chunks (the browser may split them mid-event),
   * extract complete "data: ..." lines, and dispatch them to the UI.
   *
   * Returns { answer, steps } extracted from the stream.
   */
  const parseStream = useCallback(async (response, onThought, onDone, signal) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    const steps = [];
    let answer = "";

    try {
      while (true) {
        // Check abort before each read to avoid BodyStreamBuffer abort errors
        if (signal?.aborted) break;
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Split on double newline (SSE event boundary)
        const parts = buffer.split("\n\n");
        // The last part may be incomplete — keep it in the buffer
        buffer = parts.pop() ?? "";

        for (const part of parts) {
          for (const line of part.split("\n")) {
            if (!line.startsWith("data: ")) continue;
            const raw = line.slice("data: ".length).trim();
            if (raw === "[DONE]") {
              if (!signal?.aborted) onDone({ answer, steps });
              return;
            }
            try {
              const event = JSON.parse(raw);
              switch (event.type) {
                case "thought":
                  if (event.content) onThought(event.content);
                  break;
                case "tool_call":
                  steps.push(`🔧 ${event.name}(${JSON.stringify(event.arguments ?? {})})`);
                  break;
                case "observation":
                  if (event.content) steps.push(`📋 ${event.content}`);
                  break;
                case "answer":
                  answer = event.content ?? "";
                  break;
                case "error":
                  if (!signal?.aborted) {
                    onDone({
                      answer: null,
                      errorCode: event.code,
                      errorMessage: event.message,
                      steps,
                    });
                  }
                  return;
                default:
                  break;
              }
            } catch {
              // Malformed JSON line — skip
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    // Stream ended without [DONE] sentinel — treat whatever we collected as the answer.
    // Skip the callback if the request was aborted (widget closed mid-stream).
    if (signal?.aborted) return;
    onDone({ answer: answer || null, steps });
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      const msg = (text !== undefined ? text : input).trim();
      if (!msg || isLoading) return;
      if (!token || !API_BASE) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              currentLanguage === "ar"
                ? "مساعد SellerHub غير متصل حالياً. حاول لاحقاً."
                : "SellerHub assistant is not connected. Try again later.",
            isError: true,
          },
        ]);
        return;
      }

      setInput("");
      setMessages((prev) => [...prev, { role: "user", content: msg }]);
      setIsLoading(true);

      // Cancel any previous in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const payloadMessages = buildPayloadMessages(msg);

        const res = await fetch(`${API_BASE}/invocations`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({
            messages: payloadMessages,
            threadId,
          }),
          signal: controller.signal,
        });

        if (!res.ok) {
          let errorMsg =
            currentLanguage === "ar"
              ? "حدث خطأ في الاتصال بالمساعد."
              : "Failed to reach the assistant.";
          try {
            const errData = await res.json();
            if (res.status === 401) {
              errorMsg = currentLanguage === "ar"
                ? "انتهت صلاحية جلستك. يرجى تحديث الصفحة وتسجيل الدخول مجدداً."
                : "Your session has expired. Please refresh the page and log in again.";
            } else {
              errorMsg = errData.message ?? errorMsg;
            }
          } catch { /* ignore */ }
          setMessages((prev) => [
            ...prev,
            { role: "assistant", content: errorMsg, isError: true },
          ]);
          return;
        }

        // Stream the response
        // Accumulate streaming thought tokens into a temporary "thinking" indicator
        let _thoughtBuffer = "";
        const onThought = (chunk) => {
          _thoughtBuffer += chunk;
          // Intentionally not rendering streaming thought tokens in the bubble —
          // they appear in the steps accordion after the answer arrives.
        };

        await new Promise((resolve) => {
          parseStream(
            res,
            onThought,
            ({ answer, errorCode, errorMessage, steps }) => {
              if (errorCode) {
                const msg =
                  errorCode === "GUARDRAIL_INTERVENTION"
                    ? (currentLanguage === "ar"
                        ? "لا يمكن معالجة هذا الطلب بسبب قيود سياسة المحتوى."
                        : "Your request could not be processed due to content policy restrictions.")
                    : (errorMessage ??
                        (currentLanguage === "ar" ? "حدث خطأ." : "Something went wrong."));
                setMessages((prev) => [
                  ...prev,
                  { role: "assistant", content: msg, isError: true },
                ]);
              } else if (answer) {
                setMessages((prev) => [
                  ...prev,
                  {
                    role: "assistant",
                    content: answer,
                    steps: steps?.length > 0 ? steps : undefined,
                  },
                ]);
              } else {
                setMessages((prev) => [
                  ...prev,
                  {
                    role: "assistant",
                    content:
                      currentLanguage === "ar"
                        ? "لم يتمكن المساعد من إنشاء رد."
                        : "The assistant couldn't generate a response.",
                    isError: true,
                  },
                ]);
              }
              resolve();
            },
            controller.signal
          );
        });
      } catch (err) {
        if (err?.name === "AbortError") return; // user closed the widget
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              currentLanguage === "ar" ? "خطأ في الاتصال." : "Connection error.",
            isError: true,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [input, isLoading, token, currentLanguage, threadId, buildPayloadMessages, parseStream]
  );

  const handleClose = () => {
    // Cancel any in-flight stream when the user closes the panel
    abortRef.current?.abort();
    setIsLoading(false);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleSteps = useCallback((i) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }, []);

  const positionStyle = isRTL ? { right: 20 } : { left: 20 };

  if (!user || !token) return null;

  return (
    <div
      style={{ position: "fixed", bottom: 20, zIndex: 9999, ...positionStyle }}
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* ── Chat Panel ───────────────────────────────────────────────────── */}
      {isOpen && (
        <div
          style={{
            width: 380,
            maxWidth: "calc(100vw - 40px)",
            height: 560,
            maxHeight: "calc(100vh - 100px)",
            borderRadius: 16,
            boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            background: "#fff",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "12px 16px",
              gap: 12,
              background: "#11263C",
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "#2d4a5e",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
              }}
            >
              🤖
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
                SellerHub Assistant
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: isLoading ? "#facc15" : "#4ade80",
                    transition: "background 0.3s",
                  }}
                />
                <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 11 }}>
                  {isLoading
                    ? currentLanguage === "ar"
                      ? "يفكر..."
                      : "Thinking..."
                    : currentLanguage === "ar"
                    ? "متصل · تحليلات، تقارير ومساعدة المنتجات"
                    : "Online · analytics, reports & product help"}
                </span>
              </div>
            </div>
            <button
              onClick={handleClose}
              style={{
                color: "rgba(255,255,255,0.7)",
                background: "none",
                border: "none",
                fontSize: 20,
                cursor: "pointer",
              }}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {messages.map((msg, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {/* Thinking steps accordion — temporarily hidden
                {msg.role === "assistant" && msg.steps && msg.steps.length > 0 && (
                  <div
                    style={{
                      alignSelf: isRTL ? "flex-end" : "flex-start",
                      maxWidth: "85%",
                    }}
                  >
                    <button
                      onClick={() => toggleSteps(i)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 11,
                        color: "#6b7280",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        marginBottom: 4,
                      }}
                    >
                      <span
                        style={{
                          transform: expandedSteps.has(i) ? "rotate(90deg)" : "none",
                          transition: "transform 0.2s",
                          display: "inline-block",
                        }}
                      >
                        ▶
                      </span>
                      <span>
                        🧠 {currentLanguage === "ar" ? "طريقة التفكير" : "Thinking"} (
                        {msg.steps.length})
                      </span>
                    </button>
                    {expandedSteps.has(i) && (
                      <div
                        style={{
                          background: "#f9fafb",
                          border: "1px solid #e5e7eb",
                          borderRadius: 8,
                          padding: "8px 12px",
                          marginBottom: 4,
                        }}
                      >
                        {msg.steps.map((step, si) => (
                          <div
                            key={si}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 6,
                              padding: "3px 0",
                            }}
                          >
                            <span style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>
                              ⟡
                            </span>
                            <span
                              style={{ fontSize: 11, color: "#4b5563", lineHeight: 1.4 }}
                            >
                              {step}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )} */}

                {/* Message bubble */}
                <div
                  dir={isArabicContent(msg.content) ? "rtl" : "ltr"}
                  style={{
                    maxWidth: "82%",
                    padding: "10px 14px",
                    fontSize: 13,
                    lineHeight: 1.6,
                    borderRadius: 16,
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                    unicodeBidi: "plaintext",
                    ...(msg.role === "user"
                      ? {
                          alignSelf: isRTL ? "flex-start" : "flex-end",
                          background: "#11263C",
                          color: "#fff",
                          borderBottomRightRadius: isRTL ? 16 : 4,
                          borderBottomLeftRadius: isRTL ? 4 : 16,
                        }
                      : msg.isError
                      ? {
                          alignSelf: isRTL ? "flex-end" : "flex-start",
                          background: "#fef2f2",
                          color: "#b91c1c",
                          borderBottomLeftRadius: isRTL ? 16 : 4,
                          borderBottomRightRadius: isRTL ? 4 : 16,
                        }
                      : {
                          alignSelf: isRTL ? "flex-end" : "flex-start",
                          background: "#f3f4f6",
                          color: "#1f2937",
                          borderBottomLeftRadius: isRTL ? 16 : 4,
                          borderBottomRightRadius: isRTL ? 4 : 16,
                        }),
                  }}
                >
                  {msg.content}
                </div>

                {/* Suggestion chips */}
                {msg.suggestions && msg.suggestions.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginTop: 4,
                      alignSelf: isRTL ? "flex-end" : "flex-start",
                      maxWidth: "90%",
                    }}
                  >
                    {msg.suggestions.map((chip, ci) => (
                      <button
                        key={ci}
                        onClick={() => sendMessage(chip)}
                        disabled={isLoading}
                        style={{
                          fontSize: 11,
                          padding: "5px 12px",
                          borderRadius: 20,
                          border: "1px solid #d1d5db",
                          background: "#fff",
                          color: "#11263C",
                          cursor: "pointer",
                          fontFamily: "inherit",
                          opacity: isLoading ? 0.5 : 1,
                        }}
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div
                style={{
                  display: "flex",
                  gap: 4,
                  padding: "12px 16px",
                  alignSelf: isRTL ? "flex-end" : "flex-start",
                }}
              >
                {[0, 0.15, 0.3].map((delay, i) => (
                  <span
                    key={i}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#9ca3af",
                      animation: `bounce 0.6s infinite alternate ${delay}s`,
                    }}
                  />
                ))}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: 12,
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <input
              ref={inputRef}
              type="text"
              style={{
                flex: 1,
                border: "1px solid #d1d5db",
                borderRadius: 24,
                padding: "10px 16px",
                fontSize: 13,
                outline: "none",
                fontFamily: "inherit",
                direction: isRTL ? "rtl" : "ltr",
              }}
              placeholder={
                currentLanguage === "ar"
                  ? "اسأل عن المبيعات، الإعلانات، الطلبات أو المنتجات..."
                  : "Ask about sales, ads, orders or products..."
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || isLoading}
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "#11263C",
                color: "#fff",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                cursor: "pointer",
                opacity: !input.trim() || isLoading ? 0.4 : 1,
              }}
              aria-label={currentLanguage === "ar" ? "إرسال" : "Send"}
            >
              {isRTL ? "◀" : "▶"}
            </button>
          </div>

          {/* Footer */}
          <div style={{ textAlign: "center", padding: "6px 0", borderTop: "1px solid #f3f4f6" }}>
            <span style={{ fontSize: 10, color: "#9ca3af" }}>
              Powered by Loops · AWS Bedrock
            </span>
          </div>
        </div>
      )}

      {/* ── FAB launcher ─────────────────────────────────────────────────── */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "#11263C",
            color: "#fff",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 24,
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
            cursor: "pointer",
            position: "relative",
          }}
          aria-label={currentLanguage === "ar" ? "فتح المساعد" : "Open assistant"}
        >
          💬
          <span
            style={{
              position: "absolute",
              top: 2,
              right: isRTL ? "auto" : 2,
              left: isRTL ? 2 : "auto",
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: "#4ade80",
              border: "2px solid #fff",
            }}
          />
        </button>
      )}

      <style>{`
        @keyframes bounce {
          from { transform: translateY(0); }
          to   { transform: translateY(-4px); }
        }
      `}</style>
    </div>
  );
}
